import { useCallback, useEffect, useRef, useState } from 'react'

type Pan = { x: number; y: number }

type UsePanZoomArgs = {
  zoom: number
  initialPan?: Pan
  getBounds?: () => { minX: number; maxX: number; minY: number; maxY: number }
}

type UsePanZoomReturn = {
  pan: Pan
  setPan: (p: Pan) => void
  onPointerDown: React.PointerEventHandler
  isDragging: boolean
}

const usePanZoom = ({
  zoom,
  initialPan,
  getBounds,
}: UsePanZoomArgs): UsePanZoomReturn => {
  const [pan, setPan] = useState<Pan>(initialPan ?? { x: 0, y: 0 })
  const isDraggingRef = useRef(false)
  const dragStartRef = useRef<{
    x: number
    y: number
    panX: number
    panY: number
  } | null>(null)

  const prevZoomRef = useRef(zoom)

  const onPointerDown: React.PointerEventHandler = useCallback(
    (e) => {
      e.preventDefault()
      const startX = e.clientX
      const startY = e.clientY
      isDraggingRef.current = true
      dragStartRef.current = { x: startX, y: startY, panX: pan.x, panY: pan.y }

      const handleMove = (ev: PointerEvent) => {
        if (!dragStartRef.current) return
        const dx = (ev.clientX - dragStartRef.current.x) / zoom
        const dy = (ev.clientY - dragStartRef.current.y) / zoom
        let nextX = dragStartRef.current.panX + dx
        let nextY = dragStartRef.current.panY + dy

        if (getBounds) {
          const { minX, maxX, minY, maxY } = getBounds()
          if (Number.isFinite(minX) && Number.isFinite(maxX)) {
            nextX = Math.min(maxX, Math.max(minX, nextX))
          }
          if (Number.isFinite(minY) && Number.isFinite(maxY)) {
            nextY = Math.min(maxY, Math.max(minY, nextY))
          }
        }

        setPan({ x: nextX, y: nextY })
      }
      const handleUp = () => {
        isDraggingRef.current = false
        dragStartRef.current = null
        window.removeEventListener('pointermove', handleMove)
        window.removeEventListener('pointerup', handleUp)
        window.removeEventListener('pointercancel', handleUp)
      }
      window.addEventListener('pointermove', handleMove)
      window.addEventListener('pointerup', handleUp)
      window.addEventListener('pointercancel', handleUp)
    },
    [pan.x, pan.y, zoom, getBounds],
  )

  useEffect(() => {
    if (prevZoomRef.current === zoom) return
    prevZoomRef.current = zoom
    if (!getBounds) return
    const { minX, maxX, minY, maxY } = getBounds()
    setPan((prev) => ({
      x: Math.min(maxX, Math.max(minX, prev.x)),
      y: Math.min(maxY, Math.max(minY, prev.y)),
    }))
  }, [zoom, getBounds])

  return { pan, setPan, onPointerDown, isDragging: isDraggingRef.current }
}

export default usePanZoom
